import { Injectable, signal, computed } from '@angular/core';

/**
 * RESPONSIBILITY
 * Manages user dietary preferences (dietary tags and allergen exclusions)
 * with localStorage persistence. Follows UserPreferencesService pattern.
 *
 * SIGNALS
 * - preferences: Signal<DietaryPreferences> — full preferences object
 * - dietaryTags: Signal<string[]> — selected dietary tags (vegetarian, vegan, etc.)
 * - allergenExclusions: Signal<string[]> — allergens to exclude (gluten, milk, etc.)
 * - setupComplete: Signal<boolean> — has user completed first-time setup?
 * - autoApplyFilters: Signal<boolean> — auto-apply preferences when browsing menus?
 *
 * USED BY
 * - DietaryPreferencesSetupDialogComponent (for editing preferences)
 * - MenuBrowseComponent (auto-apply filters effect)
 * - CartSummaryComponent (allergen conflict detection)
 *
 * STORAGE
 * - localStorage key: 'ct-dietary-preferences'
 * - Gracefully handles corruption and quota exceeded errors
 *
 * AI CONTEXT
 * - This service makes NO API calls itself — localStorage is the offline-first
 *   cache. ProfileSettingsSyncService hydrates it from the backend
 *   (myProfileSettings.dietary) on boot and write-throughs every change,
 *   so the preferences follow the user across devices.
 * - Dietary tags: 5 options (vegetarian, vegan, gluten_free, dairy_free, halal)
 * - Allergen exclusions: 14 UK FIC allergens (celery, gluten, crustaceans, eggs, fish, lupin, milk, molluscs, mustard, peanuts, sesame, soya, sulphur dioxide, tree nuts)
 * - setupComplete prevents showing setup dialog on every vendor selection
 */

export interface DietaryPreferences {
  /** Selected dietary tags (e.g., ['vegetarian', 'vegan']) */
  dietaryTags: string[];

  /** Allergens to exclude from menu filtering (e.g., ['gluten', 'milk']) */
  allergenExclusions: string[];

  /** Has user seen and completed (or skipped) the setup dialog? */
  setupComplete: boolean;

  /** Auto-apply saved preferences when browsing menus (default true) */
  autoApplyFilters: boolean;
}

const STORAGE_KEY = 'ct-dietary-preferences';

const DEFAULTS: DietaryPreferences = {
  dietaryTags: [],
  allergenExclusions: [],
  setupComplete: false,
  autoApplyFilters: true,
};

/**
 * Stored JSON → a `DietaryPreferences` every field of which is the right type.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY A SPREAD OVER DEFAULTS IS NOT ENOUGH
 * ─────────────────────────────────────────────────────────────────────────
 *
 * `load()` was `{ ...DEFAULTS, ...stored }`, and a spread does not skip a
 * key just because its value is null — it OVERWRITES. So a record holding
 * `dietaryTags: null`, which localStorage genuinely contains for anyone whose
 * preferences were written by an older build, replaced the default `[]` with
 * `null`. Every consumer then did `prefs.dietaryTags.length` or `.includes()`
 * on null and threw, at read time, on a device nobody could reproduce on.
 *
 * The type says `string[]`. That claim has to be made true HERE, at the one
 * boundary where untyped JSON becomes a typed object, rather than trusted
 * from a `JSON.parse` and defended at forty call sites.
 *
 * Non-array is checked, not just null: a hand-edited or half-migrated record
 * can hold a string or an object, and `Array.isArray` is the only test that
 * covers all of them. Array MEMBERS are filtered to strings for the same
 * reason — one stray number in the list is a `.toLowerCase()` away from the
 * same crash.
 */
function normalise(stored: unknown): DietaryPreferences {
  if (stored === null || typeof stored !== 'object') return { ...DEFAULTS };
  const raw = stored as Partial<Record<keyof DietaryPreferences, unknown>>;

  const strings = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];

  return {
    dietaryTags: strings(raw.dietaryTags),
    allergenExclusions: strings(raw.allergenExclusions),
    setupComplete:
      typeof raw.setupComplete === 'boolean' ? raw.setupComplete : DEFAULTS.setupComplete,
    autoApplyFilters:
      typeof raw.autoApplyFilters === 'boolean'
        ? raw.autoApplyFilters
        : DEFAULTS.autoApplyFilters,
  };
}

@Injectable({ providedIn: 'root' })
export class DietaryPreferencesService {
  private readonly _prefs = signal<DietaryPreferences>(this.load());

  /** Read-only access to all preferences */
  readonly preferences = this._prefs.asReadonly();

  // Individual computed signals for reactive binding
  readonly dietaryTags = computed(() => this._prefs().dietaryTags);
  readonly allergenExclusions = computed(() => this._prefs().allergenExclusions);
  readonly setupComplete = computed(() => this._prefs().setupComplete);
  readonly autoApplyFilters = computed(() => this._prefs().autoApplyFilters);

  /** Update one or more preferences */
  update(partial: Partial<DietaryPreferences>): void {
    const updated = { ...this._prefs(), ...partial };
    this._prefs.set(updated);
    this.save(updated);
  }

  /** Complete setup with dietary tags and allergen exclusions */
  completeSetup(dietaryTags: string[], allergenExclusions: string[]): void {
    const updated: DietaryPreferences = {
      dietaryTags,
      allergenExclusions,
      setupComplete: true,
      autoApplyFilters: true,
    };
    this._prefs.set(updated);
    this.save(updated);
  }

  /** Mark setup as complete without changing preferences (user skipped) */
  skipSetup(): void {
    this.update({ setupComplete: true });
  }

  /** Reset all preferences to defaults */
  reset(): void {
    this._prefs.set({ ...DEFAULTS });
    this.save(DEFAULTS);
  }

  /**
   * Get a DETACHED copy of current preferences.
   *
   * The arrays are copied too. A shallow `{ ...this._prefs() }` handed the
   * caller the SAME array instances the signal holds, so
   * `snapshot().dietaryTags.push(x)` mutated service state in place — no
   * `set()`, therefore no change notification to any computed/effect, and
   * no `save()`, so memory and localStorage silently diverged. That is the
   * one thing this method exists to prevent (callers wanting the live
   * object already have `preferences()`).
   */
  snapshot(): DietaryPreferences {
    const prefs = this._prefs();
    return {
      ...prefs,
      dietaryTags: [...prefs.dietaryTags],
      allergenExclusions: [...prefs.allergenExclusions],
    };
  }

  private load(): DietaryPreferences {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        return normalise(JSON.parse(raw));
      }
    } catch {
      /* corrupt data — use defaults */
    }
    return { ...DEFAULTS };
  }

  private save(prefs: DietaryPreferences): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
    } catch {
      /* storage full — ignore */
    }
  }
}
